// Session-level cache for the Amazon product catalogue (DB-backed /amazon/catalogue).
// Prefetched right after login so the ASIN lookup step's embedded catalogue renders
// instantly. Keyed by token so a session/user change invalidates it.

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

let catalogueCache = null;
let cataloguePromise = null;
let catalogueToken = null;

const loadCatalogue = async (token) => {
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const response = await fetch(`${API_BASE_URL}/amazon/catalogue`, { method: 'GET', headers });
  if (!response.ok) throw new Error('Failed to fetch catalogue');
  const data = await response.json();
  return data.products || [];
};

// Drop the cache when the session/user (token) changes.
const ensureToken = (token) => {
  if (catalogueToken !== token) {
    catalogueToken = token;
    catalogueCache = null;
    cataloguePromise = null;
  }
};

// Kick off the fetch (once) without waiting — call at login to warm the cache.
// Idempotent: a no-op if already cached or in flight.
export const prefetchCatalogue = (token) => {
  if (!token) return;
  ensureToken(token);
  if (catalogueCache || cataloguePromise) return;
  cataloguePromise = loadCatalogue(token)
    .then((products) => { catalogueCache = products; return products; })
    .catch((err) => { cataloguePromise = null; throw err; });
  // Best-effort warm-up: swallow rejection on this reference so a failed prefetch
  // doesn't surface as an unhandled promise rejection. getCatalogue keeps its own
  // handler, so the ASIN step still sees and reports real errors.
  cataloguePromise.catch(() => {});
};

// Synchronous cache peek — returns the products array if already loaded, else null.
export const getCachedCatalogue = (token) => (catalogueToken === token ? catalogueCache : null);

// Drop the cached data so the next getCatalogue() refetches (used by the Refresh button).
export const invalidateCatalogue = () => {
  catalogueCache = null;
  cataloguePromise = null;
};

// Resolve the catalogue: cached instantly, the in-flight prefetch, or a fresh request.
export const getCatalogue = async (token) => {
  ensureToken(token);
  if (catalogueCache) return catalogueCache;
  if (!cataloguePromise) {
    cataloguePromise = loadCatalogue(token)
      .then((products) => { catalogueCache = products; return products; })
      .catch((err) => { cataloguePromise = null; throw err; });
  }
  return cataloguePromise;
};
